import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTheme } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Stack,
  Divider,
  useMediaQuery
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Close as CloseIcon,
  Fullscreen as FullscreenIcon,
  Refresh as RefreshIcon,
  FullscreenExit as FullscreenExitIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import api from '../../../services/api';
import ServerDataTable from '../../ServerDataTable/ServerDataTable';
import JsonDataDisplay from '../../common/JsonDataDisplay';
import { formatDate } from '../../../utils/formatDate';
import StatusBadge from '../../StatusBadge';

export default function SyncedCustomersTab({
  tabChange
}) {
  // URL synchronization constants
  const DEFAULT_PAGE_SIZE = 10;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const navigate = useNavigate();

  // State for search and pagination
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sortConfig, setSortConfig] = useState({
    key: '',
    direction: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [viewData, setViewData] = useState(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isDialogMaximized, setIsDialogMaximized] = useState(true);
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);

  // Filter states - separate state for form inputs and applied filters
  const [filterForm, setFilterForm] = useState({
    is_deleted: '',
    last_action: '',
    updated_at_start: '',
    updated_at_end: ''
  });

  const [appliedFilters, setAppliedFilters] = useState({
    is_deleted: '',
    last_action: '',
    updated_at_start: '',
    updated_at_end: ''
  });

  // Function to update URL with filters and pagination
  const updateURLWithFilters = (filters, currentPage, currentPageSize, currentSearchTerm) => {
    const params = new URLSearchParams(location.search);

    // Clear existing filter parameters
    Object.keys(filters).forEach(key => {
      params.delete(key);
    });

    // Add non-empty filter values to URL
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== '') {
        params.set(key, value);
      }
    });

    // Add pagination parameters to URL
    if (currentPage >= 0) {
      // Convert 0-indexed internal state to 1-indexed URL
      params.set('page', (currentPage + 1).toString());
    }

    params.set('pageSize', currentPageSize.toString() || DEFAULT_PAGE_SIZE.toString());
    params.set('search', currentSearchTerm || '');

    // Update URL without reloading the page
    navigate(`${location.pathname}?${params.toString()}`, { replace: true });
  };

  // Initialize filters and pagination from URL on component mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    // Parse filter parameters
    const filtersFromURL = {
      is_deleted: params.get('is_deleted') || '',
      last_action: params.get('last_action') || '',
      updated_at_start: params.get('updated_at_start') || '',
      updated_at_end: params.get('updated_at_end') || ''
    };

    // Parse pagination parameters
    const pageParam = params.get('page');
    const pageSizeParam = params.get('pageSize');
    const searchParam = params.get('search');
    // Update page index from URL
    if (pageParam !== null) {
      // Convert 1-indexed URL to 0-indexed internal state
      const newPageIndex = parseInt(pageParam, 10) - 1;
      setPageIndex(newPageIndex);
    } else {
      // If no page parameter, reset to first page
      setPageIndex(0);
    }

    if (pageSizeParam !== null) {
      setPageSize(parseInt(pageSizeParam, 10));
    }

    if (searchParam !== null) {
      setSearchTerm(searchParam);
    }

    // Set applied filters from URL
    setAppliedFilters(filtersFromURL);
    setFilterForm(filtersFromURL);
  }, [location.search]);

  // Additional effect to ensure URL state is properly reflected in component state
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const pageParam = params.get('page');

    if (pageParam !== null) {
      const urlPageIndex = parseInt(pageParam, 10) - 1;
      // Only update if the current pageIndex doesn't match the URL
      if (pageIndex !== urlPageIndex) {
        setPageIndex(urlPageIndex);
      }
    }
  }, [location.search, pageIndex]);

  // Sync modal filters with applied filters when dialog opens
  useEffect(() => {
    if (isFilterDialogOpen) {
      setFilterForm(appliedFilters);
    }
  }, [isFilterDialogOpen, appliedFilters]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm?.trim());
      setPageIndex(0); // Reset to first pageIndex when search changes
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handle search input change with debounce
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchTerm(value);
    updateURLWithFilters(appliedFilters, 0, pageSize, value);
  };

  // Handle pagination changes
  const handlePageChange = (newPage) => {
    setPageIndex(newPage);
    updateURLWithFilters(appliedFilters, newPage, pageSize, searchTerm);
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, newPageSize, searchTerm);
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setPageIndex(0);
    updateURLWithFilters(appliedFilters, 0, pageSize, '');
  };

  // Handle filter form changes (not applied immediately)
  const handleFilterFormChange = (event) => {
    const { name, value } = event.target;
    setFilterForm((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  // Apply filters and close modal
  const handleApplyFilters = () => {
    setAppliedFilters(filterForm);
    setPageIndex(0);
    setIsFilterDialogOpen(false);
    updateURLWithFilters(filterForm, 0, pageSize, searchTerm);
  };

  // Clear filters and close modal
  const handleClearFilters = () => {
    const emptyFilters = {
      is_deleted: '',
      last_action: '',
      updated_at_start: '',
      updated_at_end: ''
    };
    setFilterForm(emptyFilters);
    setAppliedFilters(emptyFilters);
    setPageIndex(0);
    setIsFilterDialogOpen(false);
    updateURLWithFilters(emptyFilters, 0, pageSize, searchTerm);
  };

  // Open filter dialog and initialize form with current applied filters
  const handleOpenFilterDialog = () => {
    setFilterForm(appliedFilters);
    setIsFilterDialogOpen(true);
  };

  // Check if any applied filters have values (for Clear Filters button)
  const hasAppliedFilters = () => {
    return Object.values(appliedFilters).some(value => value !== '');
  };

  // Check if filter form has changed from applied filters (for Apply Filters button)
  const hasFilterFormChanges = () => {
    return Object.keys(filterForm).some(key => filterForm[key] !== appliedFilters[key]);
  };

  // Modal handlers
  const handleOpenViewDialog = (customer) => {
    setViewData(customer);
    setIsViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewData(null);
    setIsViewDialogOpen(false);
    setIsDialogMaximized(true);
  };

  const toggleDialogSize = () => {
    setIsDialogMaximized(!isDialogMaximized);
  };

  // Fetch synced customers
  const {
    data: customersData,
    isLoading,
    error,
    refetch,
    isFetching
  } = useQuery({
    queryKey: [
      'syncedCustomers',
      'erp-to-shopify',
      debouncedSearch,
      pageIndex,
      pageSize,
      tabChange,
      appliedFilters
    ],
    staleTime: 0,
    queryFn: async () => {
      const response = await api.getSyncedCustomers({
        search: debouncedSearch?.trim() || '',
        limit: pageSize,
        offset: pageIndex * pageSize,
        is_deleted: appliedFilters.is_deleted || undefined,
        last_action: appliedFilters.last_action || undefined,
        updated_at_start: appliedFilters.updated_at_start || undefined,
        updated_at_end: appliedFilters.updated_at_end || undefined
      });

      // Transform the response data
      const transformedData = {
        ...response.data,
        data: response.data.data.map((customer) => ({
          ...customer,
          formattedData: formatCustomerData(customer)[0] // Get the first item since we're processing one customer at a time
        }))
      };

      return transformedData;
    }
  });

  // Format customer data
  const formatCustomerData = (customer) => {
    if (!customer) return {};

    try {
      // Handle both single customer and array of customers
      const customers = Array.isArray(customer) ? customer : [customer];

      return customers.map((customer) => {
        const customerData =
          typeof customer.customer_data === 'string'
            ? JSON.parse(customer.customer_data)
            : customer.customer_data;

        return {
          email: customer.erp_email_id || 'N/A',
          customerCode: customer.erp_customer_id || 'N/A',
          shopifyCustomerId: customer.shopify_customer_id || 'N/A',
          lastAction: customer.last_action || 'N/A',
          updatedAt: customer.updated_at || 'N/A',
          raw: customerData
        };
      });
    } catch (error) {
      return [
        {
          email: 'Error',
          customerCode: 'Error',
          shopifyCustomerId: 'Error',
          lastAction: 'Error',
          updatedAt: 'Error',
          raw: {}
        }
      ];
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    refetch();
  };

  const columns = [
    {
      field: 'email',
      headerName: 'Email',
      width: 250,
      sortable: true,
      renderCell: ({ row }) => {
        const formattedData = formatCustomerData(row);
        return formattedData[0].email;
      }
    },
    {
      field: 'shopify_customer_id',
      headerName: 'Shopify Customer ID',
      width: 200,
      sortable: true,
      renderCell: ({ row }) => {
        const formattedData = formatCustomerData(row);
        return formattedData[0].shopifyCustomerId;
      }
    },
    {
      field: 'customer_code',
      headerName: 'Erp Customer ID',
      width: 200,
      sortable: true,
      renderCell: ({ row }) => {
        const formattedData = formatCustomerData(row);
        return formattedData[0].customerCode;
      }
    },
    {
      field: 'is_deleted',
      headerName: 'Is Deleted',
      width: 150,
      sortable: true,
      align: 'left',
      renderCell: ({ value }) => (
        value === 1 ? (
          <StatusBadge customLabel='Yes' customColor='error' customize={true} />
        ) : value === 0 ? (
          <StatusBadge customLabel='No' customColor='success' customize={true} />
        ) : (
          <StatusBadge status='N/A' />
        )
      )
    },
    {
      field: 'last_action',
      headerName: 'Last Action',
      width: 150,
      sortable: true,
      renderCell: ({ value }) => (
        <StatusBadge status={value?.toUpperCase() || 'N/A'} />
      )
    },
    {
      field: 'updated_at',
      headerName: 'Last Updated',
      width: 200,
      sortable: true,
      renderCell: ({ value }) => formatDate(value)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title='View Details'>
          <IconButton
            size='small'
            onClick={() => handleOpenViewDialog(row)}
          >
            <ViewIcon />
          </IconButton>
        </Tooltip>
      )
    }
  ];

  return (
    <Box>
      <ServerDataTable
        data={customersData?.data || []}
        columns={columns}
        sortConfig={sortConfig}
        onSort={handleSort}
        totalRows={customersData?.totalItems || 0}
        currentPage={pageIndex}
        rowsPerPage={pageSize}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handlePageSizeChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
        loading={isLoading || isFetching}
        isError={error}
        error={error}
        noDataMessage='No synced customers found'
        totalPages={Math.ceil(customersData?.totalItems / pageSize)}
        enableSearch={true}
        searchTerm={searchTerm}
        setSearchTerm={handleSearchChange}
        onClearSearch={handleClearSearch}
        searchPlaceholder='Search by Email, Shopify Customer ID, or Erp Customer ID'
        searchSize='small'
        buttons={
          <Box display='flex' gap={2} sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}>
            <Button
              variant='contained'
              startIcon={<FilterListIcon />}
              onClick={handleOpenFilterDialog}
              sx={{ width: { xs: '100% !important', sm: 'auto !important' } }}
              disabled={isFetching}
            >
              Filters
              {!isFetching && Object.values(appliedFilters).some((value) => value !== '') && (
                <Chip
                  size='small'
                  label={isFetching ? '' : Object.values(appliedFilters).filter((value) => value !== '').length}
                  color='secondary'
                  sx={{ ml: 1 }}
                  disabled={isFetching}
                />
              )}
            </Button>
            <Button sx={{ width: { xs: '100% !important', sm: 'auto !important' } }} variant='contained' color='primary' startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={isFetching}>
              Refresh
            </Button>
          </Box>
        }
      />
      {/* Filter Dialog */}
      <Dialog
        open={isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
        maxWidth='md'
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 2 }}>
          Filter Customers
          <IconButton onClick={() => setIsFilterDialogOpen(false)} size='small' sx={{ ml: 2 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Is Deleted</InputLabel>
              <Select
                name='is_deleted'
                value={filterForm.is_deleted}
                onChange={handleFilterFormChange}
                label='Is Deleted'
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='1'>Yes</MenuItem>
                <MenuItem value='0'>No</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Last Action</InputLabel>
              <Select
                name='last_action'
                value={filterForm.last_action}
                onChange={handleFilterFormChange}
                label='Last Action'
              >
                <MenuItem value=''>All</MenuItem>
                <MenuItem value='create'>Create</MenuItem>
                <MenuItem value='update'>Update</MenuItem>
              </Select>
            </FormControl>
            <Divider>Updated At Range</Divider>
            <TextField
              name='updated_at_start'
              label='From Date'
              type='date'
              value={filterForm.updated_at_start}
              onChange={handleFilterFormChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <TextField
              name='updated_at_end'
              label='To Date'
              type='date'
              value={filterForm.updated_at_end}
              onChange={handleFilterFormChange}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleClearFilters}
            color='secondary'
            variant='contained'
            disabled={!hasAppliedFilters()}
          >
            Clear Filters
          </Button>
          <Button
            onClick={handleApplyFilters}
            color='primary'
            variant='contained'
            disabled={!hasFilterFormChanges()}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog
        open={isViewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth='lg'
        fullWidth={true}
        fullScreen={isDialogMaximized}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            borderBottom: '1px solid',
            borderColor: 'divider',
            pb: 1
          }}
        >
          Customer Details
          <Box>
            <Tooltip
              title={isDialogMaximized ? 'Exit Fullscreen' : 'Fullscreen'}
            >
              <IconButton onClick={toggleDialogSize} sx={{ mr: 0.5 }}>
                {isDialogMaximized ? (
                  <FullscreenExitIcon />
                ) : (
                  <FullscreenIcon />
                )}
              </IconButton>
            </Tooltip>
            <IconButton onClick={handleCloseViewDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewData && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Email
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {viewData.erp_email_id || 'N/A'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div' sx={{ wordBreak: 'break-all !important' }}>
                  Shopify Customer ID
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant='body1' gutterBottom sx={{ mr: 1 }} color='text.secondary' component='div'>
                    {viewData.shopify_customer_id || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Last Action
                </Typography>
                <Box component='div' sx={{ mt: 1 }}>
                  <StatusBadge status={viewData.last_action} />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Created
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {formatDate(viewData.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Last Updated
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {formatDate(viewData.updated_at)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Typography variant='body2' color='text.primary' component='div'>
                  Is Deleted
                </Typography>
                <Typography variant='body1' gutterBottom color='text.secondary' component='div'>
                  {viewData.is_deleted ? <StatusBadge customLabel='Yes' customColor='error' customize={true} /> : <StatusBadge customLabel='No' customColor='success' customize={true} />}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <JsonDataDisplay data={formatCustomerData(viewData)[0]} title='Customer Data' />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseViewDialog} color='secondary' variant='contained'>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
